import React, { FC, useContext, useEffect } from "react";
import { Table, Form, Space, Popconfirm, message } from "antd";
import type { ColumnsType } from "antd/es/table";

import { SaveOutlined, EditOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";

import { CashManagementRecord, ICMBreakDownRecord } from "../../../../../types/cash-management";
import { EditableCell } from "../../EditableCell/EditableCell";
import { DataContext } from "../../../../../contexts/DataContext";
import { convertWithCommas } from "../../../../../helpers";
import { cashManagementService } from "../../../../../services/cash-management";

interface ICMBreakDownTable {
  data: ICMBreakDownRecord[];
  setData: React.Dispatch<React.SetStateAction<ICMBreakDownRecord[]>>;
  editingKey: number | undefined;
  setEditingKey: React.Dispatch<React.SetStateAction<number | undefined>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  breakDownRecord: CashManagementRecord | undefined;
  fetchData: () => Promise<void>;
  setDataToDelete: React.Dispatch<React.SetStateAction<(string | number)[]>>;
}

export const CMBreakDownTable: FC<ICMBreakDownTable> = ({
  data,
  editingKey,
  setEditingKey,
  setData,
  isLoading,
  breakDownRecord,
  fetchData,
  setIsLoading,
  setDataToDelete
}) => {
  const { codes } = useContext(DataContext);

  const getGLCode = (id: number | undefined) => {
    return codes.find((code) => code.id === id);
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "ID",
      key: "id",
      width: 80,
      render: (value: number) => (value === -1 ? "New" : value)
    },
    {
      title: "GL Code",
      dataIndex: "GLCodeID",
      key: "GLCodeID",
      width: 200,
      editable: true,
      render: (text: string, record: ICMBreakDownRecord) => {
        const gl = getGLCode(record.GLCodeID);
        if (!gl) return "";
        return `${gl.gl_code} (${gl.account_name})`;
      }
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      align: "right",
      width: 200,
      editable: true,
      render: (amount: number) => <p>${convertWithCommas(amount)}</p>
    }
  ];

  const deleteRecord = async (id: string | number) => {
    if (Number(id) > 1) setDataToDelete((ids) => [...ids, id]);
    setData((data) => data.filter((item) => item.ID !== id));
  };

  const cancel = () => {
    // if (editingKey === -1) setData(data.filter((item: ICMBreakDownRecord) => item.ID !== -1));
    setEditingKey(undefined);
  };

  const edit = (record: Partial<ICMBreakDownRecord>) => {
    setEditingKey(record.ID);
    // form.setFieldsValue({ ...record });
  };

  useEffect(() => {
    if (!editingKey) form.resetFields();
    const row = data.find((item) => item.ID === editingKey);
    if (!row) form.resetFields();
    form.setFieldsValue({ ...row });
  }, [editingKey]);

  const actionsColumn: ColumnsType<any[0]> = [
    {
      title: "Actions",
      align: "right",
      key: "actions",
      width: 90,
      className: "action-column",
      render: (_: any, record: ICMBreakDownRecord) => {
        const editable = isEditing(record);
        if (editable)
          return (
            <Space size="small">
              <SaveOutlined style={{ fontSize: "1.2rem", color: "green" }} onClick={() => form.submit()} />
              <CloseOutlined style={{ fontSize: "1.2rem", color: "red" }} onClick={cancel} />
            </Space>
          );

        return (
          <>
            <Space>
              <EditOutlined
                style={{ fontSize: "1.2rem", color: "#1890ff" }}
                disabled={editingKey !== undefined}
                onClick={() => edit(record)}
              />
              <Popconfirm placement="left" title="Delete the record?" onConfirm={() => deleteRecord(record.ID!)}>
                <DeleteOutlined style={{ fontSize: "1.2rem", color: "#ff182b" }} />
              </Popconfirm>
            </Space>
          </>
        );
      }
    }
  ];

  const isEditing = (record: ICMBreakDownRecord) => {
    if (!editingKey) return false;
    return record?.ID === editingKey;
  };

  const mergedColumns = columns.map((col: any) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: ICMBreakDownRecord) => ({
        record,
        inputType: col.dataIndex === "GLCodeID" ? "select" : col.dataIndex === "Amount" ? "number" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record)
      })
    };
  });
  const [form] = Form.useForm();

  const formOnFinish = async (values: any) => {
    setData(
      data.map((item) => {
        if (item.ID === editingKey) return { ...item, ...values };
        return item;
      })
    );
    setIsLoading(false);
    setEditingKey(undefined);
  };

  return (
    <>
      <Form form={form} onFinish={formOnFinish}>
        <Table
          loading={isLoading}
          components={{
            body: {
              cell: EditableCell
            }
          }}
          columns={[...mergedColumns, ...actionsColumn]}
          size={"small"}
          dataSource={data}
          bordered
          sticky
          pagination={false}
          key="ID"
          rowKey="ID"
        />
      </Form>
    </>
  );
};
